import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { AlertCircle, RefreshCw } from 'lucide-react';
import { Alert, AlertDescription } from "../ui/alert";
import { generateComplianceSummary } from '../../services/openai';
import { guidanceConfig } from '../../config/complianceGuidance';
import GuidanceSection from './GuidanceSection';

const ComplianceGuidance = ({ companyData }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);

  // Function to check if companyData is complete enough for analysis
  const isCompanyDataComplete = useCallback((data) => {
    console.log('🔍 Checking company data completeness:', data);
    
    if (!data) {
      console.log('❌ No company data provided');
      return false;
    }

    const requiredFields = ['legalName', 'companyDescription', 'registrationCountry', 'employeeCount'];
    const missingFields = requiredFields.filter(field => !data[field]);
    
    if (missingFields.length > 0) {
      console.log('❌ Missing required fields:', missingFields);
      return false;
    }

    console.log('✅ Company data is complete');
    return true;
  }, []);

  const generateSummary = useCallback(async () => {
    console.log('🚀 Starting compliance summary generation');
    console.log('📊 Company data:', companyData);

    if (!isCompanyDataComplete(companyData)) {
      const error = 'Please complete all required company information';
      console.error('❌', error);
      setError(error);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      console.log('🔄 Generating compliance summary for:', companyData.legalName);
      
      const result = await generateComplianceSummary(companyData);
      console.log('✅ Generated compliance summary:', result);
      
      setSummary(result);
      setError(null);
    } catch (err) {
      console.error('❌ Error generating compliance guidance:', err);
      setError(err.message || 'Failed to generate compliance guidance');
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [companyData, isCompanyDataComplete]);

  // Generate summary on initial load and when companyData changes
  useEffect(() => {
    console.log('🔄 Company data changed:', companyData);
    if (isCompanyDataComplete(companyData)) {
      generateSummary();
    }
  }, [companyData, isCompanyDataComplete, generateSummary]);

  if (!companyData) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          No company data available. Please complete the company information first.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Compliance Guidance</h2>
        <Button
          onClick={generateSummary}
          disabled={loading || !isCompanyDataComplete(companyData)}
          variant="outline"
          size="sm"
        >
          {loading ? (
            <>
              <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
              Generating...
            </>
          ) : (
            <>
              <RefreshCw className="mr-2 h-4 w-4" />
              Regenerate
            </>
          )}
        </Button>
      </div>

      {loading && !summary && (
        <Alert>
          <RefreshCw className="h-4 w-4 animate-spin" />
          <AlertDescription>
            Generating compliance guidance for {companyData.legalName}...
          </AlertDescription>
        </Alert>
      )}

      {summary && (
        <div className="space-y-4">
          {/* NIS2 Summary */}
          <Card>
            <CardContent className="pt-6 space-y-6">
              <h3 className="text-xl font-semibold mb-4">NIS2 Directive</h3>
              <div className="space-y-4">
                <p>{summary.nis2_summary.relevance_explanation}</p>
                <p><strong>Applicability:</strong> {summary.nis2_summary.applicability_type}</p>
                <p>{summary.nis2_summary.applicability_justification}</p>
                <div>
                  <strong>Key Implications:</strong>
                  <ul className="list-disc pl-6 mt-2">
                    {summary.nis2_summary.key_implications.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                </div>
                <GuidanceSection
                  guidance={guidanceConfig.nis2[summary.nis2_summary.applicability_type]}
                  title="NIS2"
                />
              </div>
            </CardContent>
          </Card>

          {/* DORA Summary */}
          <Card>
            <CardContent className="pt-6 space-y-6">
              <h3 className="text-xl font-semibold mb-4">DORA Regulation</h3>
              <div className="space-y-4">
                <p>{summary.dora_summary.relevance_explanation}</p>
                <p><strong>Applicability:</strong> {summary.dora_summary.applicability_type}</p>
                <p>{summary.dora_summary.applicability_justification}</p>
                <div>
                  <strong>Key Implications:</strong>
                  <ul className="list-disc pl-6 mt-2">
                    {summary.dora_summary.key_implications.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                </div>
                <GuidanceSection
                  guidance={guidanceConfig.dora[summary.dora_summary.applicability_type]}
                  title="DORA"
                />
              </div>
            </CardContent>
          </Card>

          {/* GDPR Summary */}
          <Card>
            <CardContent className="pt-6 space-y-6">
              <h3 className="text-xl font-semibold mb-4">GDPR</h3>
              <div className="space-y-4">
                <p>{summary.gdpr_summary.relevance_explanation}</p>
                <p><strong>Role:</strong> {summary.gdpr_summary.role_classification}</p>
                <p>{summary.gdpr_summary.role_justification}</p>
                <div>
                  <strong>Key Implications:</strong>
                  <ul className="list-disc pl-6 mt-2">
                    {summary.gdpr_summary.key_implications.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                </div>
                <GuidanceSection
                  guidance={guidanceConfig.gdpr[summary.gdpr_summary.role_classification]}
                  title="GDPR"
                />
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ComplianceGuidance;
